/**
 * Admin Courses API Service - TICKET LTPKD7
 * 
 * Servicio para gestionar cursos desde la interfaz de administración.
 * Conecta con los endpoints del backend (TICKET XNRQF3).
 * 
 * Base path: /api/admin/courses
 */

import apiClient from './apiClient';

/**
 * Obtener todos los cursos (draft, published, archived)
 * GET /api/admin/courses
 * 
 * @param {Object} options - Opciones de filtrado
 * @param {string} options.status - Filtrar por status: 'draft' | 'published' | 'archived'
 * @returns {Promise<Array>} Lista de cursos
 */
export const getAllCourses = async (options = {}) => {
  try {
    const params = {};
    if (options.status) {
      params.status = options.status;
    }

    const response = await apiClient.get('/admin/courses', { params });
    return response.data.courses || [];
  } catch (error) {
    console.error('❌ Error obteniendo cursos:', error);
    throw error;
  }
};

/**
 * Crear un nuevo curso
 * POST /api/admin/courses
 * 
 * @param {Object} payload - Datos del curso
 * @param {string} payload.courseId - ID único (slug format: design-thinking)
 * @param {string} payload.title - Título del curso
 * @param {string} payload.shortDescription - Descripción corta
 * @param {number} payload.estimatedHours - Horas estimadas
 * @param {Array<string>} payload.skills - Skills que desarrolla
 * @returns {Promise<Object>} Curso creado
 */
export const createCourse = async (payload) => {
  try {
    const response = await apiClient.post('/admin/courses', payload);
    return response.data.course;
  } catch (error) {
    console.error('❌ Error creando curso:', error);
    
    // Mejorar mensaje de error para el UI
    if (error.response?.status === 409) {
      throw new Error('Ya existe un curso con ese ID');
    }
    if (error.response?.status === 400) {
      const errorData = error.response.data;
      if (errorData.error === 'INVALID_COURSE_ID') {
        throw new Error('El ID del curso solo puede contener letras minúsculas, números y guiones');
      }
      throw new Error(errorData.message || 'Error de validación');
    }
    
    throw new Error('No se pudo crear el curso. Intenta nuevamente.');
  }
};

/**
 * Archivar un curso (cambia status a 'archived')
 * POST /api/admin/courses/:courseId/archive
 * 
 * @param {string} courseId - ID del curso a archivar
 * @returns {Promise<Object>} Curso archivado
 */
export const archiveCourse = async (courseId) => {
  try {
    const response = await apiClient.post(`/admin/courses/${courseId}/archive`);
    return response.data.course;
  } catch (error) {
    console.error(`❌ Error archivando curso ${courseId}:`, error);
    
    if (error.response?.status === 400 && error.response?.data?.error === 'ALREADY_ARCHIVED') {
      throw new Error('El curso ya está archivado');
    }
    if (error.response?.status === 404) {
      throw new Error('Curso no encontrado');
    }
    
    throw new Error('No se pudo archivar el curso. Intenta nuevamente.');
  }
};

/**
 * Obtener un curso específico por ID
 * GET /api/admin/courses/:courseId
 * 
 * @param {string} courseId - ID del curso
 * @returns {Promise<Object>} Curso encontrado
 */
export const getCourseById = async (courseId) => {
  try {
    const response = await apiClient.get(`/admin/courses/${courseId}`);
    return response.data.course;
  } catch (error) {
    console.error(`❌ Error obteniendo curso ${courseId}:`, error);
    
    if (error.response?.status === 404) {
      throw new Error('Curso no encontrado');
    }
    
    throw error;
  }
};

/**
 * Editar un curso existente
 * PUT /api/admin/courses/:courseId
 * 
 * @param {string} courseId - ID del curso a editar
 * @param {Object} updates - Campos a actualizar
 * @returns {Promise<Object>} Curso actualizado
 */
export const updateCourse = async (courseId, updates) => {
  try {
    const response = await apiClient.put(`/admin/courses/${courseId}`, updates);
    return response.data.course;
  } catch (error) {
    console.error(`❌ Error actualizando curso ${courseId}:`, error);
    
    if (error.response?.status === 404) {
      throw new Error('Curso no encontrado');
    }
    if (error.response?.status === 403) {
      throw new Error('No tienes permisos para realizar esta acción');
    }
    
    throw new Error('No se pudo actualizar el curso. Intenta nuevamente.');
  }
};

export default {
  getAllCourses,
  createCourse,
  archiveCourse,
  getCourseById,
  updateCourse
};
